import { FC, useCallback, useMemo } from 'react'
import styles from 'components/features/pfm/OpportunityReviewCommentsPane/OpportunityReviewCommentsPane.styl'
import Button from 'components/features/pfm/Button/Button'
import { useStoreDispatch, useStoreSelector, useShallowSelector } from 'helpers/reactReduxHelpers'
import { killFloatingPanesActionCreator, modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { FloatingPane, OpportunityReviewFloatingPane } from 'interfaces/floatingPanes'
import { createFloatingPaneByTypeSelector } from 'selectors/floatingPanesSelectors'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { connectLoadable } from 'helpers/loadableHelpers'
import { WithSocialInsightUid, SocialInsightUid } from 'interfaces/socialInsights'
import { GuardsParam, SimpleGuard } from 'interfaces/guards'
import { getCommentsActionCreator, WithOrdering } from 'actions/feedActions'
import PageLoadingOverlay from 'components/atoms/preloaders/PageLoadingOverlay'
import { createSocialInsightSelector, createFeedAllCommentUidsByPostUidSelector } from 'selectors/feedSelectors'
import { StrippedFragment } from 'components/features/pfm/StrippedFragment/StrippedFragment'
import { defaultMemoize } from 'reselect'
import { createInsightBySocialInsightSelector } from 'selectors/socialInsightSelectors'
import InsightFooterContainer from 'components/features/insights/InsightFooter/InsightFooterContainer'
import omit from 'lodash/omit'
import { SocialInsightReviewList } from 'components/features/socialFeed/SocialInsightReviewList/SocialInsightReviewList'
import { useIsPublicPage } from 'helpers/pageHelpers'

const TYPED_PANE: Pick<FloatingPane, 'type'> = {
  type: 'opportunity-review',
}

type OpportunityReviewCommentsPaneProps = {

}

type CommentsGuardProps =
  & WithSocialInsightUid
  & WithOrdering

const createCommentsGuards: GuardsParam<CommentsGuardProps> = (): SimpleGuard<CommentsGuardProps>[] => {
  const commentUidsSelector = createFeedAllCommentUidsByPostUidSelector()
  const getUidObj = defaultMemoize((uid: SocialInsightUid) => ({ postUid: uid }))

  return [{
    name: 'Opportunity Reviews Guards',
    checker: (state, { socialInsightUid }) => {
      return Boolean(commentUidsSelector(state, getUidObj(socialInsightUid)))
    },
    loader: (dispatch, { socialInsightUid, ordering }) => {
      dispatch(getCommentsActionCreator.request({ postUid: socialInsightUid, ordering }))
    },
  }]
}

const RendererWithGuards = connectLoadable<{}, {}, CommentsGuardProps, {}>(
  createCommentsGuards, PageLoadingOverlay
)(undefined)(StrippedFragment)


export const OpportunityReviewCommentsPane: FC<OpportunityReviewCommentsPaneProps> = (props) => {
  const {} = props

  const isPublic = useIsPublicPage()

  const paneSelector = useMemo(createFloatingPaneByTypeSelector, [])
  const pane = useShallowSelector((state) => paneSelector(state, TYPED_PANE)) as OpportunityReviewFloatingPane | null

  const dispatch = useStoreDispatch()
  const handleWriteReview = useCallback(() => {
    if (!pane) {
      return
    }

    dispatch(
      modifyFloatingPaneActionCreator('opportunity-review', {
        type: 'opportunity-review',
        payload: {
          ...pane.payload,
          mode: 'write-review',
        },
      })
    )
  }, [dispatch, pane])

  const handleClose = useCallback(() => {
    dispatch(killFloatingPanesActionCreator('opportunity-review'))
  }, [dispatch])

  const socialInsightSelector = useMemo(createSocialInsightSelector, [])
  const fakeUid = pane ? pane.payload.uid : ''
  const uidObj = useMemo(() => ({ postUid: fakeUid }), [fakeUid])
  const socialInsight = useStoreSelector((state) => socialInsightSelector(state, uidObj))

  const insightSelector = useMemo(createInsightBySocialInsightSelector, [])
  const insight = useStoreSelector((state) => insightSelector(state, uidObj))

  const headerChild = useMemo(() => {
    if (!insight) {
      return null
    }

    return (
      <div className={ styles.header }>
        <div className={ styles.title }>
          { insight.title }
        </div>
        { insight.cta && (
          <div className={ styles.cta }>
            <InsightFooterContainer
              insight={{
                ...insight,
                cta: omit(insight.cta, 'dismiss'),
              }}
              fullWidth
              buttonSize='tiny'
            />
          </div>
        )}
      </div>
    )
  }, [insight])

  const footerChild = useMemo(() => {
    if (isPublic) {
      return null
    }
    return (
      <>
        <div className={ styles.writeReview }>
          <Button
            size='medium'
            text='Write review'
            qaMarker='save-button'
            fullWidth
            onClick={ handleWriteReview }
          />
        </div>
        <Button
          size='medium'
          style='hollow'
          text='Close'
          qaMarker='save-button'
          fullWidth
          onClick={ handleClose }
        />
      </>
    )
  }, [handleClose, handleWriteReview, isPublic])

  return useMemo(() => {
    if (!pane || !socialInsight) {
      return null
    }

    const { uid } = pane.payload

    const { commentsOrdering } = socialInsight

    return (
      <FloatingPaneLayout
        type='opportunity-review'
        header={ headerChild }
        footer={ footerChild }
        onClose={ handleClose }
      >
        <div className={ styles.content }>
          <RendererWithGuards socialInsightUid={ uid } ordering={ commentsOrdering }>
            <SocialInsightReviewList postUid={ uid } />
          </RendererWithGuards>
        </div>
      </FloatingPaneLayout>
    )
  }, [pane, socialInsight, headerChild, footerChild, handleClose])
}
